/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  MRT_ColumnDef,
  MRT_TableState,
  MRT_VisibilityState,
  MRT_TableOptions,
  MRT_RowData,
} from "mantine-react-table"
import { useUserLocalStorage } from "../use-user-local-storage"
import { InfiniteScrollingTable } from "."
import { PageRequest } from "../../../../@client/Theasoft_ApiServer__Utils_Pagination"
import { useBreakpoints } from "../../../../utils/use-breakpoints"
import { Pagination } from "../../../../utils/Pagination"
import { Sort } from "../../../../@client/Theasoft_Data_Queries"

interface Props<
  TQueryFn extends (...args: any[]) => Promise<Pagination<any>>,
  TData extends MRT_RowData = Awaited<ReturnType<TQueryFn>>["data"],
> {
  queryFn: TQueryFn
  columnDefinitions: MRT_ColumnDef<TData>[]
  onClickRow?: (row: TData) => void
  defaultSorting: Sort[]
  defaultColumVisibility?: MRT_VisibilityState
  tableOptions?: Omit<MRT_TableOptions<TData>, "data" | "columns">
  initialTableState?: Partial<MRT_TableState<TData>>
  getParams: (pageRequest: PageRequest) => Parameters<TQueryFn>[0]
  globalFilter: string
  onGlobalFilterChange?: (value: string) => void
  sorting: Sort[]
  onSortingChange?: (value: Sort[]) => void
}

export interface ExtendedTableProps<
  TQueryFn extends (...args: any[]) => Promise<Pagination<any>>,
  TData extends MRT_RowData = Awaited<ReturnType<TQueryFn>>["data"],
> extends Omit<
    Props<TQueryFn, TData>,
    "url" | "columnDefinitions" | "defaultColumnVisibility" | "defaultSorting"
  > {}

export function ExtendedInfiniteScrollingTableNewApi<
  TQueryFn extends (...args: any[]) => Promise<Pagination<any>>,
  TData extends MRT_RowData = Awaited<ReturnType<TQueryFn>>["data"],
>({
  queryFn,
  columnDefinitions,
  onClickRow,
  defaultSorting,
  defaultColumVisibility,
  tableOptions: options,
  initialTableState,
  getParams,
  globalFilter,
  onGlobalFilterChange,
  sorting,
  onSortingChange,
}: Props<TQueryFn, TData>) {
  const [tableState, setTableState] = useUserLocalStorage<
    Partial<MRT_TableState<TData>>
  >({
    key: queryFn.toString() + "table-state",
    defaultValue: {
      columnVisibility: defaultColumVisibility ?? {},
      ...initialTableState,
    },
  })
  const { xs, sm } = useBreakpoints()
  const isMobile = xs || sm

  const handleSortingChange = (value: Sort[]) => {
    onSortingChange && onSortingChange(value)
    // setSorting(value)
  }

  const handleGlobalFilterChange = (value: string) => {
    onGlobalFilterChange && onGlobalFilterChange(value)
    // setGlobalFilter(value)
  }

  const [columnVisibility, setColumnVisibility] = React.useState(
    tableState.columnVisibility
  )

  React.useEffect(() => {
    setTableState({ ...tableState, columnVisibility: columnVisibility })
  }, [columnVisibility])

  const tableOptions = React.useMemo<Omit<MRT_TableOptions<TData>, "data">>(
    () => ({
      columns: columnDefinitions,
      manualFiltering: true,
      manualSorting: true,
      enableColumnResizing: true,
      enableColumnDragging: false,
      enableColumnOrdering: true,
      enableColumnActions: false,
      enableDensityToggle: false,
      enableHeaderActionsHoverReveal: true,
      onGlobalFilterChange: (updaterOrValue) =>
        typeof updaterOrValue === "function"
          ? handleGlobalFilterChange(updaterOrValue(tableState.globalFilter))
          : handleGlobalFilterChange(updaterOrValue),
      onSortingChange: (updaterOrValue) =>
        typeof updaterOrValue === "function"
          ? handleSortingChange(updaterOrValue(tableState.sorting ?? []))
          : handleSortingChange(updaterOrValue),
      onShowGlobalFilterChange: (updaterOrValue) =>
        setTableState((prev) => ({
          ...prev,
          showGlobalFilter:
            typeof updaterOrValue === "function"
              ? updaterOrValue(prev.showGlobalFilter ?? false)
              : updaterOrValue,
        })),
      onColumnVisibilityChange: (updaterOrValue) =>
        setColumnVisibility(
          typeof updaterOrValue === "function"
            ? updaterOrValue(columnVisibility ?? {})
            : updaterOrValue
        ),
      onColumnSizingChange: (updaterOrValue) =>
        setTableState((prev) => ({
          ...prev,
          columnSizing:
            typeof updaterOrValue === "function"
              ? updaterOrValue(prev.columnSizing ?? {})
              : updaterOrValue,
        })),
      onColumnOrderChange: (updaterOrValue) =>
        setTableState((prev) => ({
          ...prev,
          columnOrder:
            typeof updaterOrValue === "function"
              ? updaterOrValue(prev.columnOrder ?? [])
              : updaterOrValue,
        })),
      onDensityChange: (updaterOrValue) =>
        setTableState((prev) => ({
          ...prev,
          density:
            typeof updaterOrValue === "function"
              ? updaterOrValue(prev.density ?? "md")
              : updaterOrValue,
        })),
      state: {
        ...tableState,
        globalFilter,
        sorting,
        columnVisibility,
      },
      ...options,
    }),
    [
      tableState,
      options,
      columnDefinitions,
      defaultSorting,
      isMobile,
      columnVisibility,
    ]
  )

  return (
    <InfiniteScrollingTable
      tableOptions={tableOptions}
      getParams={getParams}
      queryFn={queryFn}
      onClickRow={onClickRow}
    />
  )
}
